import React from 'react';
import { Workflow, Play, Pause, Edit, Trash2, Clock, Zap, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Plus } from 'lucide-react';
import { TRIGGER_TYPES, ACTION_TYPES } from './workflowConstants';

export const WorkflowStats = ({ workflows }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    <Card><CardContent className="pt-6"><div className="text-center">
      <p className="text-3xl font-bold text-[#001A47]">{workflows.length}</p>
      <p className="text-sm text-gray-500">Total Workflows</p>
    </div></CardContent></Card>
    <Card><CardContent className="pt-6"><div className="text-center">
      <p className="text-3xl font-bold text-green-600">{workflows.filter(w => w.is_active).length}</p>
      <p className="text-sm text-gray-500">Active</p>
    </div></CardContent></Card>
    <Card><CardContent className="pt-6"><div className="text-center">
      <p className="text-3xl font-bold text-orange-600">{workflows.filter(w => !w.is_active).length}</p>
      <p className="text-sm text-gray-500">Paused</p>
    </div></CardContent></Card>
    <Card><CardContent className="pt-6"><div className="text-center">
      <p className="text-3xl font-bold text-[#fb6402]">{workflows.reduce((sum, w) => sum + (w.executions_count || 0), 0)}</p>
      <p className="text-sm text-gray-500">Total Executions</p>
    </div></CardContent></Card>
  </div>
);

export const WorkflowList = ({ workflows, onToggle, onEdit, onDelete, onCreateNew }) => {
  const getTriggerInfo = (triggerType) => 
    TRIGGER_TYPES.find(t => t.value === triggerType) || { label: triggerType, icon: Workflow, color: 'text-gray-500' };

  if (workflows.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Workflow className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600">No workflows yet</h3>
          <p className="text-gray-500 mb-4">Create your first automated workflow to engage customers</p>
          <Button onClick={onCreateNew}><Plus className="h-4 w-4 mr-2" />Create Workflow</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {workflows.map((workflow) => {
        const triggerInfo = getTriggerInfo(workflow.trigger_type);
        const TriggerIcon = triggerInfo.icon;
        
        return (
          <Card key={workflow.workflow_id} className={`border-l-4 ${workflow.is_active ? 'border-l-green-500' : 'border-l-gray-300'}`}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 rounded-full bg-gray-100 ${triggerInfo.color}`}>
                      <TriggerIcon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{workflow.name}</h3>
                      <p className="text-sm text-gray-500">{triggerInfo.label}</p>
                    </div>
                    <Badge variant={workflow.is_active ? 'default' : 'secondary'} className={workflow.is_active ? 'bg-green-100 text-green-800' : ''}>
                      {workflow.is_active ? 'Active' : 'Paused'}
                    </Badge>
                  </div>
                  
                  {workflow.description && <p className="text-gray-600 mb-3">{workflow.description}</p>}

                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                    {workflow.trigger_delay_minutes > 0 && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {workflow.trigger_delay_minutes >= 60 
                          ? `${Math.floor(workflow.trigger_delay_minutes / 60)}h delay`
                          : `${workflow.trigger_delay_minutes}m delay`}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Zap className="h-4 w-4" />
                      {workflow.actions?.length || 0} action{(workflow.actions?.length || 0) !== 1 ? 's' : ''}
                    </span>
                    {workflow.executions_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Play className="h-4 w-4" />
                        {workflow.executions_count} executions
                      </span>
                    )}
                  </div>

                  {workflow.actions && workflow.actions.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {workflow.actions.map((action, idx) => {
                        const actionInfo = ACTION_TYPES.find(a => a.value === action.type);
                        const ActionIcon = actionInfo?.icon || Settings;
                        return (
                          <Badge key={`${action.type}-${idx}`} variant="outline" className="flex items-center gap-1">
                            <ActionIcon className="h-3 w-3" />
                            {actionInfo?.label || action.type}
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <Switch checked={workflow.is_active} onCheckedChange={() => onToggle(workflow.workflow_id)} />
                  <Button variant="ghost" size="icon" onClick={() => onEdit(workflow)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="text-red-500" onClick={() => onDelete(workflow.workflow_id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
